import type { Cohort, Invite2, MentorLoad, Person2 } from "@/app/peopleQueries";
import { plural } from "@/lib/plural";
import { Card, Dot, TONE_TEXT } from "./shared";
import { MAIN_STEPS, PERMS, loadTone, loadWidth, medianGates, shortDate, students, type Scope } from "./model";

/** Нагрузка менторов: полоса n/max, предупреждение о перекосе — текст сервера дословно. */
export function MentorLoadCard({ load }: { load: MentorLoad }) {
  return (
    <Card eyebrow="распределение по менторам" testId="load-card">
      {load.mentors.length === 0 ? (
        <p className="m-0 font-mono text-[11.5px] text-mut">в школе пока нет менторов — пригласите с ролью «ментор»</p>
      ) : (
        <ul className="m-0 flex list-none flex-col gap-3 p-0">
          {load.mentors.map((m) => {
            const tone = loadTone(load, m.students);
            return (
              <li key={m.mentor.id} className="flex flex-col gap-1.5" data-testid="load-row" data-count={m.students}>
                <span className="flex items-baseline justify-between gap-3">
                  <span className="text-[13px] text-ink" data-testid="load-name">
                    {m.mentor.name}
                  </span>
                  <span className={`font-mono text-[11.5px] ${TONE_TEXT[tone]}`} data-testid="load-count">
                    {students(m.students)}
                  </span>
                </span>
                <span className="block h-[5px] overflow-hidden rounded-[2px] bg-card2" role="img" aria-label={`${m.students} из ${load.max}`}>
                  <span className="block h-full" style={{ width: `${loadWidth(m.students, load.max)}%`, background: `var(--${tone})` }} />
                </span>
              </li>
            );
          })}
        </ul>
      )}
      <p className={`m-0 mt-3.5 flex items-start gap-2 text-[12px] leading-[1.45] ${load.skewed ? "text-warn" : "text-ink2"}`} data-testid="load-note" data-skewed={load.skewed}>
        <Dot tone={load.skewed ? "warn" : "acc"} />
        <span>{load.note}</span>
      </p>
      {load.unassigned > 0 && (
        <p className="m-0 mt-2 font-mono text-[11.5px] text-ink2" data-testid="load-unassigned">
          без ментора: {students(load.unassigned)}
        </p>
      )}
    </Card>
  );
}

/** Права ролей — дословно. */
export function PermsCard() {
  return (
    <Card eyebrow="права ролей" testId="perms-card">
      <dl className="m-0 grid grid-cols-[92px_minmax(0,1fr)] gap-x-2.5 gap-y-2.5">
        {PERMS.map((p) => (
          <div key={p.role} className="contents">
            <dt className={`font-mono text-[11.5px] ${TONE_TEXT[p.tone]}`}>{p.role}</dt>
            <dd className="m-0 text-[12.5px] leading-[1.45] text-ink2">{p.can}</dd>
          </div>
        ))}
      </dl>
    </Card>
  );
}

/** Факты о текущем режиме — считаются из данных (в макете были захардкожены). */
export function FactsCard({ scope, cohort, rows, invites }: { scope: Scope; cohort?: Cohort; rows: Person2[]; invites: Invite2[] }) {
  const median = medianGates(rows);
  const pending = invites.filter((i) => i.state === "ожидает" && (scope === "all" || (scope === "nocohort" ? !i.cohort : i.cohort === cohort?.name))).length;
  const facts: { k: string; v: string; tone?: string }[] = [];
  if (cohort?.starts_on) facts.push({ k: "старт", v: shortDate(cohort.starts_on) + "." + cohort.starts_on.slice(0, 4) });
  if (cohort) facts.push({ k: "неделя", v: cohort.status === "finished" ? "завершена" : cohort.week > 0 ? `${cohort.week}-я` : "не началась" });
  facts.push({ k: "маршрут", v: `${MAIN_STEPS} шагов` });
  facts.push({ k: "медиана прогресса", v: median === null ? "—" : `${median} ${plural(median, "шаг", "шага", "шагов")}` });
  facts.push({ k: "ожидают приглашения", v: String(pending), tone: pending > 0 ? "warn" : undefined });
  return (
    <Card eyebrow={scope === "all" ? "школа" : scope === "nocohort" ? "вне когорт" : "когорта"} testId="facts-card">
      <dl className="m-0 flex flex-col gap-2">
        {facts.map((f) => (
          <div key={f.k} className="flex items-baseline justify-between gap-3">
            <dt className="font-mono text-[11px] text-mut">{f.k}</dt>
            <dd className={`m-0 font-mono text-[12px] ${f.tone ? TONE_TEXT[f.tone] : "text-ink2"}`}>{f.v}</dd>
          </div>
        ))}
      </dl>
    </Card>
  );
}
